import * as React from "react";
import { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  CircularProgress, // PDFダウンロード処理中のインジケータ用
} from "@mui/material";
import {
  useLogout,
  useNotify,
  usePermissions,
  useTranslate,
} from "react-admin";
import { format } from "date-fns"; // 日付フォーマット用 (dayjsもプロジェクトに含まれていますが、date-fnsも一般的です)
import { hasPermission } from "../../authProvider";
import { ExpireTermsStatus, ROLE_ADMIN } from "../constants/define";

export interface TermsOfServiceDialogProps {
  /** ダイアログの表示状態 */
  open: boolean;
  /**
   * ダイアログが閉じる際に呼び出されるコールバック
   * @param agreed 同意した場合は true
   */
  onClose: (agreed: boolean) => void;

  /** 文言キー */
  prefix: string;

  /** 利用規約のテキスト (長文になることを想定) */
  termsText: string;

  /** 同意期限 */
  agreementDeadline: Date | null;

  /** 利用規約の更新ステータス */
  expireTermStatus: ExpireTermsStatus;

  /** PDFダウンロード処理を実行する関数 (オプション) */
  onDownloadPdf?: () => Promise<void>;
}

export const TermsOfServiceDialog: React.FC<TermsOfServiceDialogProps> = ({
  open,
  onClose,
  prefix,
  termsText,
  agreementDeadline,
  expireTermStatus,
  onDownloadPdf,
}) => {
  const { permissions } = usePermissions();
  const [agreed, setAgreed] = useState(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const translate = useTranslate();
  const logout = useLogout();
  const notify = useNotify();

  // ダイアログが開かれたら、同意チェックボックスをリセット
  useEffect(() => {
    if (open) {
      setAgreed(false);
    }
  }, [open]);

  const handleAgreeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAgreed(event.target.checked);
  };

  const handleOk = async () => {
    if (agreed) {
      onClose(true);
    }
  };

  const handleLogout = async () => {
    await logout();
    onClose(false);
  };

  const handleCancel = async () => {
    onClose(false);
  };

  const handleDialogClose = async () => {
    if (isReadOnly) {
      onClose(false);
    }
  };

  const handleDownload = async () => {
    if (onDownloadPdf) {
      setIsDownloading(true);
      try {
        await onDownloadPdf();
      } catch (error) {
        notify("PDFのダウンロードに失敗しました。", { type: "error" });
      } finally {
        setIsDownloading(false);
      }
    }
  };

  const isAdmin = hasPermission(permissions, ROLE_ADMIN);
  const isReadOnly = expireTermStatus == ExpireTermsStatus.None;
  // 契約更新(contractPeriod)ダイアログは期限切れでも「後で」で閉じられる（他ダイアログは従来通り強制ログアウト）
  const forceLogoutOnExpired =
    expireTermStatus === ExpireTermsStatus.Expired &&
    prefix !== "contractPeriod";

  const dialogTitle = isReadOnly
    ? translate(`terms-dialog.${prefix}.titleNone`)
    : expireTermStatus === ExpireTermsStatus.Expired
      ? translate(`terms-dialog.${prefix}.titleExpired`, {
          _: translate(`terms-dialog.${prefix}.title`),
        })
      : translate(`terms-dialog.${prefix}.title`);
  let dialogBody: string;
  if (expireTermStatus === ExpireTermsStatus.Expired) {
    dialogBody = isAdmin
      ? translate(`terms-dialog.${prefix}.description`)
      : translate(`terms-dialog.${prefix}.descriptionForUser`);
  } else {
    dialogBody = isAdmin
      ? translate(`terms-dialog.${prefix}.descriptionWarning`)
      : translate(`terms-dialog.${prefix}.descriptionWarningForUser`);
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={expireTermStatus !== ExpireTermsStatus.Expired}
      onClose={handleDialogClose}
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent dividers>
        {" "}
        {/* dividers をつけると上下に罫線が入る */}
        {!isReadOnly && (
          <DialogContentText component="div" sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              {dialogBody}
            </Typography>
            {agreementDeadline && (
              <Typography variant="body2" color="textSecondary">
                {translate(`terms-dialog.${prefix}.deadline`)}{" "}
                {format(agreementDeadline, "yyyy/MM/dd")}
              </Typography>
            )}
          </DialogContentText>
        )}
        {isAdmin && (
          <>
            <Box sx={{ mb: 2, display: "flex", justifyContent: "right" }}>
              <Button
                variant="outlined"
                onClick={handleDownload}
                disabled={isDownloading}
                startIcon={
                  isDownloading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {translate(`terms-dialog.${prefix}.download`)}
              </Button>
            </Box>
            <Box
              sx={{
                border: "1px solid rgba(0, 0, 0, 0.12)",
                padding: 2,
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                whiteSpace: "pre-wrap", // 改行とスペースを保持
                borderRadius: 1,
              }}
            >
              <Typography
                component="div"
                variant="body2"
                sx={{ whiteSpace: "pre-wrap" }}
              >
                {termsText}
              </Typography>
            </Box>
            {!isReadOnly && (
              <FormControlLabel
                control={
                  <Checkbox checked={agreed} onChange={handleAgreeChange} />
                }
                label={translate(`terms-dialog.${prefix}.aggreement`)}
                sx={{ mt: 1, display: "flex", justifyContent: "left" }}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-end", padding: "16px 24px" }}>
        {!isReadOnly ? (
          <>
            {isAdmin ? (
              <>
                {forceLogoutOnExpired ? (
                  <Button onClick={handleLogout}>
                    {translate(`terms-dialog.logout`)}
                  </Button>
                ) : (
                  <Button onClick={handleCancel}>
                    {translate(`terms-dialog.later`)}
                  </Button>
                )}
                <Button
                  onClick={handleOk}
                  variant="contained"
                  disabled={!agreed || isDownloading}
                >
                  {translate(`terms-dialog.ok`)}
                </Button>
              </>
            ) : (
              <>
                {forceLogoutOnExpired ? (
                  <Button onClick={handleLogout}>
                    {translate(`terms-dialog.logout`)}
                  </Button>
                ) : (
                  <Button onClick={handleCancel}>
                    {translate(`terms-dialog.close`)}
                  </Button>
                )}
              </>
            )}
          </>
        ) : (
          <Button onClick={handleCancel}>
            {translate(`terms-dialog.close`)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
